import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { PRIMARY_SLOTS, MONTHS_DE } from "../lib/slots";
import { daysInMonth, holidaysForYear, isWeekend, weekdayShortDe, ymd } from "../lib/calendar";
import { fmtDateDE } from "../lib/finance";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { buildOccupancy, buildMilestones, buildSpecialDaysMap } from "./WallPlanner";
import { PlanningIcon } from "../lib/planningIcons";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const SLOT_LABEL_W_BASE = 168;    // fits "#12 " + two 6-char labels + separator
const DAY_COL_W_BASE = 28;
const HEADER_H_BASE = 40;
const ROW_H_BASE = 30;

/** Enumerate individual days across a rolling 12-month window starting at (year, month). */
const LEAD_DAYS = 3; // Kalendertage vor dem Anker-Monat, damit „Heute" die 4. Spalte sein kann

function rollingDays(startYear, startMonth) {
  const out = [];
  const lead = new Date(startYear, startMonth, 1 - LEAD_DAYS);
  for (let i = 0; i < LEAD_DAYS; i++) {
    const dt = new Date(lead.getFullYear(), lead.getMonth(), lead.getDate() + i);
    out.push({ y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate(), key: ymd(dt.getFullYear(), dt.getMonth(), dt.getDate()), date: dt });
  }
  let y = startYear, m = startMonth;
  for (let i = 0; i < 12; i++) {
    const dc = daysInMonth(y, m);
    for (let d = 1; d <= dc; d++) {
      out.push({ y, m, d, key: ymd(y, m, d), date: new Date(y, m, d) });
    }
    m++;
    if (m > 11) { m = 0; y++; }
  }
  return out;
}

/**
 * HorizontalPlanner — 4-quadrant layout with JS-synchronized scroll.
 *   • Fixed corner (top-left)      — sticky month indicator
 *   • Fixed left column            — slot #1–#12 + sticky short labels of continuing projects
 *   • Scrollable header (top)      — day columns (X-synced with body)
 *   • Scrollable body (main)       — day cells, milestone diamonds, in-bar short labels
 * The four sections stay perfectly aligned because horizontal scroll is JS-synced
 * from the body → header, and vertical scroll from the body → left column.
 */
const HorizontalPlanner = forwardRef(function HorizontalPlanner(
  { startYear, startMonth, projects, specialDays, onCellClick, onMilestoneDrop, onPlanningNoteDrop, onProjectSelect, sortMode = "milestone", zoom = 1 },
  ref,
) {
  // Effective, zoom-scaled dimensions. All layout math derives from these
  // so date/milestone alignment stays correct at every scale.
  const z = zoom === 2 ? 2 : zoom === 1.5 ? 1.5 : 1;
  const SLOT_LABEL_W = Math.round(SLOT_LABEL_W_BASE * z);
  const DAY_COL_W = Math.round(DAY_COL_W_BASE * z);
  const HEADER_H = Math.round(HEADER_H_BASE * z);
  const ROW_H = Math.round(ROW_H_BASE * z);
  const IN_BAR_LABEL_FS = Math.round(11 * z);
  const SLOT_LABEL_FS = Math.round(11 * z);
  const SLOT_NUM_FS = Math.round(10 * z);
  const HEADER_FS = Math.round(9 * z);
  const MILESTONE_SIZE = Math.round(12 * z);
  const days = useMemo(() => rollingDays(startYear, startMonth), [startYear, startMonth]);
  const occupancy = useMemo(() => buildOccupancy(projects), [projects]);
  const milestones = useMemo(() => buildMilestones(projects), [projects]);
  // Map: `${dateKey}|${slot}` -> [{ projectId, noteIdx, icon, title, date }]
  const planningIcons = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      if (p.status === "archived") continue;
      if (!Array.isArray(p.planning_notes)) continue;
      p.planning_notes.forEach((n, idx) => {
        if (!n?.show_in_planner || !n?.date) return;
        if (p.start_date && n.date < p.start_date) return;
        if (p.end_date && n.date > p.end_date) return;
        const key = `${n.date}|${p.slot}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({
          projectId: p.id,
          noteIdx: idx,
          icon: n.planner_icon || "production",
          title: n.title || "",
          date: n.date,
        });
      });
    }
    return map;
  }, [projects]);
  const specialDaysMap = useMemo(() => buildSpecialDaysMap(specialDays), [specialDays]);

  const holidaysByYear = useMemo(() => {
    const yrs = new Set(days.map((x) => x.y));
    const map = new Map();
    for (const y of yrs) map.set(y, holidaysForYear(y));
    return map;
  }, [days]);

  const todayKey = useMemo(() => {
    const t = new Date();
    return ymd(t.getFullYear(), t.getMonth(), t.getDate());
  }, []);

  const sortedSlotList = useMemo(() => {
    if (sortMode === "default") return PRIMARY_SLOTS;
    // "milestone"      → main milestone + visible planning notes (notes affect order)
    // "milestone_main" → main milestone only (notes still render but do not sort)
    const earliest = new Map();
    const consider = (slotN, date, isMain) => {
      const cur = earliest.get(slotN);
      if (!cur || date < cur.date || (date === cur.date && isMain && !cur.isMain)) {
        earliest.set(slotN, { date, isMain });
      }
    };
    for (const p of projects) {
      if (p.status === "archived") continue;
      if (p.milestone_date && p.milestone_date >= todayKey) {
        consider(p.slot, p.milestone_date, true);
      }
      if (sortMode === "milestone" && Array.isArray(p.planning_notes)) {
        for (const n of p.planning_notes) {
          if (!n?.show_in_planner || !n?.date) continue;
          if (n.date < todayKey) continue;
          if (p.start_date && n.date < p.start_date) continue;
          if (p.end_date && n.date > p.end_date) continue;
          consider(p.slot, n.date, false);
        }
      }
    }
    return [...PRIMARY_SLOTS].sort((a, b) => {
      const da = earliest.get(a.n);
      const db = earliest.get(b.n);
      if (da && db) {
        if (da.date !== db.date) return da.date < db.date ? -1 : 1;
        if (da.isMain !== db.isMain) return da.isMain ? -1 : 1;
        return a.n - b.n;
      }
      if (da) return -1;
      if (db) return 1;
      return a.n - b.n;
    });
  }, [projects, todayKey, sortMode]);

  const bodyRef = useRef(null);
  const headerRef = useRef(null);
  const leftColRef = useRef(null);

  const [leftmostIdx, setLeftmostIdx] = useState(0);

  const syncScroll = useCallback(() => {
    const b = bodyRef.current;
    if (!b) return;
    if (headerRef.current) headerRef.current.scrollLeft = b.scrollLeft;
    if (leftColRef.current) leftColRef.current.scrollTop = b.scrollTop;
    const idx = Math.max(0, Math.min(days.length - 1, Math.floor(b.scrollLeft / DAY_COL_W)));
    setLeftmostIdx(idx);
  }, [days.length, DAY_COL_W]);

  const scrollToTodayNow = useCallback(() => {
    const b = bodyRef.current;
    if (!b) return;
    const idx = days.findIndex((x) => x.key === todayKey);
    if (idx < 0) { b.scrollLeft = 0; syncScroll(); return; }
    // Heute exakt als 4. vollständig sichtbare Tagesspalte (3 Kalendertage davor).
    const target = Math.max(0, (idx - LEAD_DAYS) * DAY_COL_W);
    b.scrollTo({ left: target, behavior: "auto" });
    // scrollTo async — force a sync tick.
    requestAnimationFrame(syncScroll);
  }, [days, todayKey, syncScroll, DAY_COL_W]);

  useEffect(() => {
    scrollToTodayNow();
  }, [startYear, startMonth]);

  // Preserve currently visible date position when the zoom level changes.
  // On zoom-change the DAY_COL_W has already been recomputed for the new zoom,
  // while `leftmostIdx` still points to the pre-zoom leftmost visible day. We
  // manually adjust scrollLeft so the same date remains at the left edge.
  const prevZoomRef = useRef(z);
  useEffect(() => {
    if (prevZoomRef.current === z) return;
    prevZoomRef.current = z;
    const b = bodyRef.current;
    if (!b) return;
    b.scrollLeft = leftmostIdx * DAY_COL_W;
    requestAnimationFrame(syncScroll);
  }, [z, DAY_COL_W, leftmostIdx, syncScroll]);

  const scrollDateAsFourth = useCallback((dateKey) => {
    const b = bodyRef.current;
    if (!b) return;
    const idx = days.findIndex((x) => x.key === dateKey);
    if (idx < 0) return;
    b.scrollTo({ left: Math.max(0, (idx - 3) * DAY_COL_W), behavior: "smooth" });
  }, [days]);

  useImperativeHandle(ref, () => ({
    scrollToToday: () => scrollToTodayNow(),
  }));

  // Precompute a per-slot segment map used both for in-bar label placement and
  // for the sticky-slot-label rendering while scrolling.
  const segmentsBySlot = useMemo(() => {
    const map = new Map();
    for (const s of sortedSlotList) {
      const segs = [];
      let prevId = null;
      let segStart = null;
      let segProj = null;
      let segHasConflict = false;
      for (let i = 0; i < days.length; i++) {
        const projs = occupancy.get(days[i].key)?.get(s.n);
        const pid = projs?.[0]?.id || null;
        const conflict = (projs?.length || 0) > 1;
        if (pid !== prevId) {
          if (prevId && segStart !== null) {
            segs.push({ startIdx: segStart, endIdx: i - 1, project: segProj, hasConflict: segHasConflict });
          }
          prevId = pid;
          segStart = pid ? i : null;
          segProj = pid ? projs[0] : null;
          segHasConflict = pid ? conflict : false;
        } else if (pid && conflict) {
          segHasConflict = true;
        }
      }
      if (prevId && segStart !== null) {
        segs.push({ startIdx: segStart, endIdx: days.length - 1, project: segProj, hasConflict: segHasConflict });
      }
      map.set(s.n, segs);
    }
    return map;
  }, [sortedSlotList, days, occupancy]);

  // Sticky labels next to slot number: projects whose real start is BEFORE the
  // leftmost visible date but that still continue THROUGH it.
  const stickyLabelsBySlot = useMemo(() => {
    const leftKey = days[leftmostIdx]?.key;
    const map = new Map();
    if (!leftKey) return map;
    for (const p of projects) {
      if (p.status === "archived") continue;
      const raw = (p.planner_short_label || "").trim();
      if (!raw) continue;
      if (!p.start_date || !p.end_date) continue;
      if (p.start_date >= leftKey) continue;   // start still visible → in-bar
      if (p.end_date < leftKey) continue;       // already ended
      if (!map.has(p.slot)) map.set(p.slot, []);
      map.get(p.slot).push({ label: raw, id: p.id });
    }
    return map;
  }, [projects, leftmostIdx, days]);

  const cols = days.length;
  const bodyWidth = cols * DAY_COL_W;
  const bodyHeight = sortedSlotList.length * ROW_H;

  const visibleMonth = days[leftmostIdx] || days[0] || { m: startMonth, y: startYear };

  return (
    <TooltipProvider>
      <div className="flex-1 h-full flex flex-col overflow-hidden bg-slate-50" data-testid="horizontal-planner">
        {/* Row 1: header (corner + day headers) */}
        <div className="flex" style={{ height: HEADER_H, flexShrink: 0 }}>
          {/* Corner — sticky month indicator */}
          <div
            className="border-b border-r border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-[9px] tracking-wide uppercase text-slate-500"
            style={{ width: SLOT_LABEL_W, flexShrink: 0 }}
            data-testid="horizontal-planner-current-month"
            title={`${MONTHS_DE[visibleMonth.m]} ${visibleMonth.y}`}
          >
            <span style={{ fontFamily: "Manrope, ui-sans-serif" }} className="font-semibold text-slate-700">
              {MONTHS_DE[visibleMonth.m].slice(0, 3)}
            </span>
            <span className="tabular-nums opacity-70">{visibleMonth.y}</span>
          </div>
          {/* Scrollable header content — X-synced with body */}
          <div ref={headerRef} className="flex-1 overflow-hidden">
            <div style={{ width: bodyWidth, height: HEADER_H, display: "flex" }}>
              {days.map((x, idx) => {
                const wknd = isWeekend(x.date);
                const holidayName = holidaysByYear.get(x.y)?.get(x.key);
                const specialTitles = specialDaysMap.get(x.key);
                const isSpecial = Array.isArray(specialTitles) && specialTitles.length > 0;
                const isToday = x.key === todayKey;
                const showMonth = idx === 0 || days[idx - 1].m !== x.m;
                const title = [
                  ...(holidayName ? [holidayName] : []),
                  ...(isSpecial ? specialTitles : []),
                  ...(isToday && !holidayName && !isSpecial ? ["Heute"] : []),
                ].join(" · ");
                return (
                  <div
                    key={x.key}
                    className={`shrink-0 border-r border-b border-slate-200 flex flex-col items-center justify-center tabular-nums transition-colors ${
                      isToday
                        ? "bg-slate-900 text-white font-semibold"
                        : isSpecial
                        ? "bg-green-100/80 text-slate-700"
                        : holidayName
                        ? "bg-rose-50/40 text-rose-500"
                        : wknd
                        ? (x.date.getDay() === 0 ? "bg-slate-200/70 text-slate-500" : "bg-slate-100/60 text-slate-400")
                        : "bg-slate-50 text-slate-500"
                    }`}
                    style={{ width: DAY_COL_W, height: HEADER_H, cursor: "pointer", fontSize: HEADER_FS }}
                    title={title}
                    data-testid={isToday ? "today-day-label" : `hp-header-${x.key}`}
                    onDoubleClick={(e) => { e.preventDefault(); scrollDateAsFourth(x.key); }}
                  >
                    {showMonth && (
                      <span
                        className={`text-[9px] tracking-wide uppercase ${isToday ? "text-white/80" : "text-slate-400"}`}
                        style={{ fontFamily: "Manrope, ui-sans-serif" }}
                      >
                        {MONTHS_DE[x.m].slice(0, 3)}
                      </span>
                    )}
                    <span className="leading-tight">{String(x.d).padStart(2, "0")}</span>
                    <span className="text-[8px] leading-none opacity-80">{weekdayShortDe(x.date)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Row 2: body (left col + scrollable content) */}
        <div className="flex flex-1 overflow-hidden">
          {/* Fixed left slot column — Y-synced with body */}
          <div
            ref={leftColRef}
            className="overflow-hidden bg-white border-r border-slate-200"
            style={{ width: SLOT_LABEL_W, flexShrink: 0 }}
          >
            <div style={{ height: bodyHeight, position: "relative" }}>
              {sortedSlotList.map((s, rowIdx) => {
                const stickyLbls = stickyLabelsBySlot.get(s.n) || [];
                return (
                  <div
                    key={s.n}
                    className="border-b border-slate-200 flex items-center px-2 tabular-nums text-slate-600"
                    style={{ height: ROW_H, position: "absolute", top: rowIdx * ROW_H, left: 0, right: 0, fontSize: SLOT_NUM_FS }}
                    data-testid={`hp-slot-row-${s.n}`}
                  >
                    <span
                      className="rounded-sm border border-black/10 shrink-0"
                      style={{ backgroundColor: s.color, width: Math.round(10 * z), height: Math.round(10 * z) }}
                      data-testid={`hp-slot-swatch-${s.n}`}
                    />
                    <span className="ml-1.5 shrink-0">#{s.n}</span>
                    {stickyLbls.length > 0 && (
                      <span
                        className="ml-1.5 font-semibold text-slate-900"
                        title={stickyLbls.map((x) => x.label).join(" · ")}
                        data-testid={`sticky-slot-label-${s.n}`}
                        style={{ letterSpacing: "0.02em", fontSize: SLOT_LABEL_FS, whiteSpace: "nowrap" }}
                      >
                        {stickyLbls.slice(0, 2).map((x) => x.label).join(" · ") +
                          (stickyLbls.length > 2 ? " · …" : "")}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Scrollable body — the single scroll driver */}
          <div ref={bodyRef} className="flex-1 overflow-auto" onScroll={syncScroll}>
            <div style={{ width: bodyWidth, height: bodyHeight, position: "relative" }}>
              {sortedSlotList.map((s, rowIdx) => {
                const segments = segmentsBySlot.get(s.n) || [];
                return (
                  <React.Fragment key={s.n}>
                    {/* Day cells */}
                    {days.map((x, colIdx) => {
                      const wknd = isWeekend(x.date);
                      const holidayName = holidaysByYear.get(x.y)?.get(x.key);
                      const isSpecial = specialDaysMap.has(x.key);
                      const isToday = x.key === todayKey;
                      const projs = occupancy.get(x.key)?.get(s.n);
                      const milestone = milestones.get(`${x.key}|${s.n}`);
                      const cellTestId = `horizontal-planner-cell-${x.key}-${s.n}`;
                      const baseBg = holidayName
                        ? "rgba(255,241,242,0.35)"
                        : wknd
                        ? (x.date.getDay() === 0 && !isSpecial ? "rgba(226,232,240,0.7)" : "rgba(241,245,249,0.6)")
                        : "#ffffff";
                      const left = colIdx * DAY_COL_W;
                      const top = rowIdx * ROW_H;

                      if (projs && projs.length > 0) {
                        const primary = projs[0];
                        const conflict = projs.length > 1;
                        const milestoneProjectId = milestone ? milestone.project.id : null;
                        const iconEntries = planningIcons.get(`${x.key}|${s.n}`) || [];
                        const cellDrop = (onMilestoneDrop || onPlanningNoteDrop) ? {
                          onDragOver: (e) => {
                            const dt = e.dataTransfer;
                            if (!dt) return;
                            if (dt.types?.includes("application/x-milestone") ||
                                dt.types?.includes("application/x-planning-note")) {
                              e.preventDefault();
                              dt.dropEffect = "move";
                            }
                          },
                          onDrop: (e) => {
                            const noteId = e.dataTransfer.getData("application/x-planning-note");
                            if (noteId && onPlanningNoteDrop) {
                              const [pid, nidxStr] = noteId.split("|");
                              const nidx = parseInt(nidxStr, 10);
                              if (pid && Number.isFinite(nidx)) {
                                e.preventDefault();
                                onPlanningNoteDrop(pid, nidx, x.key);
                                return;
                              }
                            }
                            const id = e.dataTransfer.getData("application/x-milestone");
                            if (id && onMilestoneDrop) {
                              e.preventDefault();
                              onMilestoneDrop(id, x.key);
                            }
                          },
                        } : {};

                        const cellBody = (
                          <button
                            onClick={conflict ? undefined : () => onCellClick(x.key, s.n, primary, projs)}
                            className="border-r border-b border-slate-100 cursor-pointer outline-none"
                            style={{ position: "absolute", left, top, width: DAY_COL_W, height: ROW_H, backgroundColor: baseBg }}
                            data-testid={cellTestId}
                            data-conflict={conflict ? "true" : undefined}
                            data-milestone={milestone ? "true" : undefined}
                            aria-label={`${primary.name} — ${primary.client}${conflict ? " (Konflikt)" : ""}${milestone ? ` · Meilenstein: ${milestone.title}` : ""}`}
                            {...cellDrop}
                          >
                            <span
                              className="absolute inset-0"
                              style={{
                                backgroundColor: primary.planner_dimmed ? "#94a3b8" : s.color,
                                opacity: (primary.planner_dimmed || primary.status === "paused" ? 0.45 : 1) * (isSpecial ? 0.5 : 1),
                              }}
                            />
                            {conflict && (
                              <>
                                <span
                                  className="absolute inset-0 pointer-events-none"
                                  style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(190,18,60,0.85) 0 2px, transparent 2px 6px)" }}
                                />
                                <span
                                  className="absolute inset-0 pointer-events-none"
                                  style={{ boxShadow: "inset 0 0 0 2px #be123c" }}
                                />
                              </>
                            )}
                            {milestone && (
                              <span
                                draggable={Boolean(onMilestoneDrop)}
                                onDragStart={(e) => {
                                  e.stopPropagation();
                                  e.dataTransfer.setData("application/x-milestone", milestoneProjectId);
                                  e.dataTransfer.effectAllowed = "move";
                                }}
                                onClick={(e) => e.stopPropagation()}
                                title="Meilenstein ziehen, um Datum zu ändern"
                                className="absolute left-1/2 top-1/2"
                                style={{
                                  width: MILESTONE_SIZE, height: MILESTONE_SIZE, background: "#fff",
                                  border: "1.5px solid #0f172a", borderRadius: 2,
                                  transform: "translate(-50%, -50%) rotate(45deg)",
                                  boxShadow: "0 0 0 1.5px rgba(255,255,255,0.6)",
                                  cursor: onMilestoneDrop ? "grab" : "default",
                                  zIndex: 3,
                                }}
                                data-testid={`milestone-marker-${milestone.project.id}`}
                              />
                            )}
                            {iconEntries.map((ent, ei) => {
                              const noteCount = iconEntries.length;
                              const hasMs = Boolean(milestone);
                              let offsetX = 0;
                              if (noteCount === 1 && hasMs) offsetX = 9;
                              else if (noteCount === 2 && !hasMs) offsetX = ei === 0 ? -6 : 6;
                              else if (noteCount === 2 && hasMs) offsetX = ei === 0 ? -10 : 10;
                              else if (noteCount > 2) {
                                const step = 7;
                                offsetX = -((noteCount - 1) * step) / 2 + ei * step;
                              }
                              const badgeSize = Math.round(14 * z);
                              const iconSize = Math.round(11 * z);
                              return (
                                <span
                                  key={`${ent.projectId}-${ent.noteIdx}`}
                                  draggable={Boolean(onPlanningNoteDrop)}
                                  onDragStart={(e) => {
                                    e.stopPropagation();
                                    e.dataTransfer.setData("application/x-planning-note", `${ent.projectId}|${ent.noteIdx}`);
                                    e.dataTransfer.effectAllowed = "move";
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  title={`${ent.title} · ${fmtDateDE(ent.date)}`}
                                  className="absolute grid place-items-center"
                                  style={{
                                    left: "50%", top: "50%",
                                    transform: `translate(calc(-50% + ${offsetX * z}px), -50%)`,
                                    width: badgeSize, height: badgeSize, borderRadius: 999,
                                    background: "rgba(255,255,255,0.92)",
                                    boxShadow: "0 0 0 1px rgba(15,23,42,0.4)",
                                    cursor: onPlanningNoteDrop ? "grab" : "default",
                                    zIndex: 2,
                                  }}
                                  data-testid={`planning-icon-${ent.projectId}-${ent.noteIdx}`}
                                >
                                  <PlanningIcon icon={ent.icon} size={iconSize} />
                                </span>
                              );
                            })}
                            {isToday && (
                              <span
                                className="absolute inset-y-0 left-0 pointer-events-none"
                                style={{ width: 2, backgroundColor: "#0f172a" }}
                              />
                            )}
                          </button>
                        );

                        if (conflict) {
                          return (
                            <Popover key={cellTestId}>
                              <PopoverTrigger asChild>{cellBody}</PopoverTrigger>
                              <PopoverContent side="top" className="w-64 p-2" data-testid={`conflict-popover-${x.key}-${s.n}`}>
                                <div className="text-rose-600 font-semibold mb-1.5 text-xs">
                                  ⚠ Konflikt — {projs.length} Projekte
                                </div>
                                {projs.map((p) => (
                                  <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => onProjectSelect?.(p)}
                                    className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-slate-100 transition-colors"
                                    data-testid={`conflict-project-${p.id}`}
                                  >
                                    <div className="font-medium text-slate-900 text-sm">{p.name}</div>
                                    <div className="text-xs text-slate-500">{p.client}</div>
                                    <div className="text-[11px] text-slate-400 tabular-nums">
                                      {fmtDateDE(p.start_date)} → {fmtDateDE(p.end_date)}
                                    </div>
                                  </button>
                                ))}
                              </PopoverContent>
                            </Popover>
                          );
                        }

                        return (
                          <Tooltip key={cellTestId} delayDuration={80}>
                            <TooltipTrigger asChild>{cellBody}</TooltipTrigger>
                            <TooltipContent side="top" className="bg-slate-900 text-white text-xs max-w-xs">
                              {milestone && (
                                <div className="text-amber-200 font-semibold mb-1 flex items-baseline gap-1.5">
                                  <span aria-hidden>◆</span>
                                  <span>{milestone.title} — {milestone.project.name}</span>
                                </div>
                              )}
                              {projs.map((p, i) => (
                                <div key={p.id} className={i > 0 ? "mt-1.5 pt-1.5 border-t border-white/10" : ""}>
                                  <div className="font-medium">{p.name}</div>
                                  <div className="opacity-80">{p.client}</div>
                                  <div className="opacity-60 tabular-nums mt-0.5">{fmtDateDE(p.start_date)} → {fmtDateDE(p.end_date)}</div>
                                </div>
                              ))}
                            </TooltipContent>
                          </Tooltip>
                        );
                      }

                      return (
                        <button
                          key={cellTestId}
                          onClick={() => onCellClick(x.key, s.n, null)}
                          className="border-r border-b border-slate-100 hover:bg-slate-100 transition-colors"
                          style={{ position: "absolute", left, top, width: DAY_COL_W, height: ROW_H, backgroundColor: baseBg }}
                          data-testid={cellTestId}
                          aria-label={`Slot ${s.n} am ${x.key} frei`}
                          onDragOver={(e) => {
                            if (!onMilestoneDrop && !onPlanningNoteDrop) return;
                            const dt = e.dataTransfer;
                            if (!dt) return;
                            if (dt.types?.includes("application/x-milestone") ||
                                dt.types?.includes("application/x-planning-note")) {
                              e.preventDefault();
                              dt.dropEffect = "move";
                            }
                          }}
                          onDrop={(e) => {
                            const noteId = e.dataTransfer.getData("application/x-planning-note");
                            if (noteId && onPlanningNoteDrop) {
                              const [pid, nidxStr] = noteId.split("|");
                              const nidx = parseInt(nidxStr, 10);
                              if (pid && Number.isFinite(nidx)) {
                                e.preventDefault();
                                onPlanningNoteDrop(pid, nidx, x.key);
                                return;
                              }
                            }
                            const id = e.dataTransfer.getData("application/x-milestone");
                            if (id && onMilestoneDrop) {
                              e.preventDefault();
                              onMilestoneDrop(id, x.key);
                            }
                          }}
                        >
                          {isToday && (
                            <span
                              className="absolute inset-y-0 left-0 pointer-events-none"
                              style={{ width: 2, backgroundColor: "#0f172a" }}
                            />
                          )}
                        </button>
                      );
                    })}
                    {/* In-bar labels — only at the project's REAL start (segment start matches project.start_date). */}
                    {segments.map((seg) => {
                      const label = (seg.project.planner_short_label || "").trim();
                      if (!label) return null;
                      if (seg.hasConflict) return null;
                      const segStartKey = days[seg.startIdx]?.key;
                      if (!segStartKey) return null;
                      // Only if the segment starts at the project's actual start_date.
                      if (segStartKey !== seg.project.start_date) return null;
                      const len = seg.endIdx - seg.startIdx + 1;
                      if (len < 2) return null;
                      return (
                        <div
                          key={`sl-${s.n}-${seg.startIdx}`}
                          className="pointer-events-none flex items-center"
                          style={{
                            position: "absolute",
                            left: seg.startIdx * DAY_COL_W,
                            top: rowIdx * ROW_H,
                            width: len * DAY_COL_W,
                            height: ROW_H,
                            paddingLeft: 3,
                            overflow: "hidden",
                            zIndex: 2,
                          }}
                          data-testid={`short-label-${seg.project.id}-${seg.startIdx}`}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              background: "rgba(255,255,255,0.9)",
                              color: "#0f172a",
                              fontWeight: 700,
                              fontSize: IN_BAR_LABEL_FS,
                              lineHeight: 1,
                              letterSpacing: "0.02em",
                              padding: "3px 5px",
                              borderRadius: 3,
                              whiteSpace: "nowrap",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
                            }}
                          >
                            {label}
                          </span>
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
});

export default HorizontalPlanner;
